import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useFormikContext } from 'formik'
import { ButtonBase, IconButton, makeStyles } from '@material-ui/core'
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined'
import CloseIcon from '@material-ui/icons/Close'

import { isFormFieldDisabled } from '../../../../../utils/helpers/isFieldDisabled'
import connectedGameFieldStyles from './connectedGameField.styles'
import ConnectedGameDialog from './ConnectedGameDialog'

const formatGameDateTime = (startDateTime) => {
  if (!startDateTime) return ''
  return new Date(startDateTime).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Candidates carry home/away team names; an already-connected game (event.pairedGame) carries a
// precomposed title instead. Both render as "Home vs Away — dd/mm/yy hh:mm".
const getGameName = (option) =>
  option.title ?? `${option.homeTeamName} vs ${option.awayTeamName}`

const getOptionLabel = (option) =>
  option ? `${getGameName(option)} — ${formatGameDateTime(option.startDateTime)}` : ''

// Lets the user connect the club game being created/edited to a federation game of one of the
// club team's paired season teams. The picker is a modal (see ConnectedGameDialog); this renders
// the read-only trigger field showing the current selection. Candidates are supplied by the parent
// (useGamePairingCandidates) so it can hide the field via renderValue when there are none.
const ConnectedGameField = ({ options, state }) => {
  const { values, setFieldValue } = useFormikContext()

  const useStyles = makeStyles(connectedGameFieldStyles)
  const classes = useStyles()

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  if (!options || options.length === 0) return null

  const disabled = isFormFieldDisabled(state)
  const selectedOption = options.find((game) => game.gameEventId === values.pairedGameId) ?? null

  const handleSelect = (gameEventId) => {
    setFieldValue('pairedGameId', gameEventId)
    setIsDialogOpen(false)
  }

  const handleClear = (event) => {
    event.stopPropagation()
    setFieldValue('pairedGameId', null)
  }

  return (
    <div className='div-flex'>
      <LinkOutlinedIcon className='icon' style={{ fill: 'grey' }} />
      <div className={classes.wrapper}>
        <ButtonBase
          className={classes.field}
          disabled={disabled}
          onClick={() => setIsDialogOpen(true)}
          focusRipple
        >
          <span className={classes.label}>Connected game</span>
          <span className={selectedOption ? classes.value : classes.placeholder}>
            {selectedOption ? getOptionLabel(selectedOption) : 'Select a connected game'}
          </span>
        </ButtonBase>
        {selectedOption && !disabled && (
          <IconButton
            size='small'
            className={classes.clear}
            onClick={handleClear}
            aria-label='Clear connected game'
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        )}
      </div>

      <ConnectedGameDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        options={options}
        selectedGameId={values.pairedGameId}
        onSelect={handleSelect}
        getOptionLabel={getOptionLabel}
      />
    </div>
  )
}

ConnectedGameField.propTypes = {
  // renderValue (consumed by FormContainer to skip an empty row) is passed by the parent.
  renderValue: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.object),
  state: PropTypes.string,
}

export default ConnectedGameField
